import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  getRouteTemplates, getMercaderistasWithRoutes, createAssignment, deleteAssignment,
  getUserRoutes, downloadAssignmentsTemplate, uploadAssignmentsFile, updateAssignmentOrder
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  Upload, Download, Route, User, Store, AlertTriangle, 
  Check, Plus, Trash2, MapPin, Users, ChevronUp, ChevronDown,
  RotateCcw, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

export default function AssignmentsPage() {
  const [mercaderistas, setMercaderistas] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Selected mercaderista for viewing routes
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRoutesData, setUserRoutesData] = useState(null);
  
  // Add route dialog
  const [showAddRouteDialog, setShowAddRouteDialog] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  
  const fileInputRef = useRef(null);

  const loadInitialData = useCallback(async () => {
    try {
      const [mercaRes, templatesRes] = await Promise.all([
        getMercaderistasWithRoutes(),
        getRouteTemplates()
      ]);
      setMercaderistas(mercaRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const loadUserRoutes = async (userId) => {
    try {
      const response = await getUserRoutes(userId);
      setUserRoutesData(response.data);
    } catch (error) {
      toast.error('Error cargando rutas del usuario');
      setUserRoutesData(null);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    loadUserRoutes(user.id);
  };

  const handleOpenAddRouteDialog = () => {
    setSelectedTemplateId('');
    setShowAddRouteDialog(true);
  };

  const handleAddRoute = async () => {
    if (!selectedTemplateId) {
      toast.error('Selecciona una plantilla');
      return;
    }

    try {
      await createAssignment({
        user_id: selectedUser.id,
        route_template_id: selectedTemplateId,
        route_order: 0  // Auto-calculate
      });
      toast.success('Ruta agregada');
      setShowAddRouteDialog(false);
      loadInitialData();
      loadUserRoutes(selectedUser.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error agregando ruta');
    }
  };

  const handleRemoveRoute = async (assignmentId) => {
    if (!window.confirm('¿Quitar esta ruta del mercaderista?')) return;

    try {
      await deleteAssignment(assignmentId);
      toast.success('Ruta removida');
      loadInitialData();
      loadUserRoutes(selectedUser.id);
    } catch (error) {
      toast.error('Error removiendo ruta');
    }
  };

  const handleMoveRouteUp = async (index) => {
    if (index === 0 || !userRoutesData?.routes) return;
    await swapRouteOrder(index, index - 1);
  };

  const handleMoveRouteDown = async (index) => {
    if (!userRoutesData?.routes || index === userRoutesData.routes.length - 1) return;
    await swapRouteOrder(index, index + 1);
  };

  const swapRouteOrder = async (fromIndex, toIndex) => {
    const routes = [...userRoutesData.routes];
    const [movedRoute] = routes.splice(fromIndex, 1);
    routes.splice(toIndex, 0, movedRoute);
    
    try {
      const updates = routes.map((route, idx) => 
        updateAssignmentOrder(route.assignment_id, idx + 1)
      );
      await Promise.all(updates);
      await loadUserRoutes(selectedUser.id);
      toast.success('Orden de rutas actualizado');
    } catch (error) {
      toast.error('Error reordenando rutas');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadAssignmentsTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_asignaciones.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Plantilla descargada');
    } catch (error) {
      toast.error('Error descargando plantilla');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadAssignmentsFile(file);
      const { created, skipped, errors } = response.data;
      if (errors?.length > 0) {
        toast.warning(`Creadas: ${created}, Omitidas: ${skipped}, Errores: ${errors.length}`);
      } else {
        toast.success(`Creadas: ${created}, Omitidas: ${skipped}`);
      }
      loadInitialData();
    } catch (error) {
      toast.error('Error procesando archivo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Get templates not yet assigned to selected user
  const getAvailableTemplates = () => {
    if (!selectedUser || !userRoutesData?.routes) return templates;
    const assignedTemplateIds = new Set(userRoutesData.routes.map(r => r.template_id));
    return templates.filter(t => !assignedTemplateIds.has(t.id));
  };

  // Separate users by assignment status
  const usersWithRoutes = mercaderistas.filter(m => m.has_routes);
  const usersWithoutRoutes = mercaderistas.filter(m => !m.has_routes);

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => <Skeleton key={`assignments-skeleton-${i}`} className="h-48" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assignments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Asignación de Rutas</h1>
          <p className="text-slate-500">Asigna múltiples rutas a cada mercaderista en orden</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-btn">
            <Download className="w-4 h-4 mr-2" /> Plantilla
          </Button>
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-excel-btn"
          >
            <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Importando...' : 'Importar'}
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".xlsx,.xls"
            className="hidden"
          />
        </div>
      </div>

      {/* Info Alert */}
      <Alert className="border-blue-200 bg-blue-50">
        <RotateCcw className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Flujo cíclico:</strong> Al terminar todas las tiendas de una ruta, el mercaderista pasa a la siguiente. 
          Al completar todas las rutas, vuelve a la primera (Ruta 1 → Ruta 2 → ... → Ruta 1).
        </AlertDescription>
      </Alert>

      {templates.length === 0 && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Nota:</strong> Primero debes crear plantillas de ruta antes de poder asignarlas.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Mercaderistas List */}
        <div className="space-y-4">
          {/* Users with routes */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  Con Rutas Asignadas
                </CardTitle>
                <Badge variant="secondary">{usersWithRoutes.length}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[250px]">
                {usersWithRoutes.length === 0 ? (
                  <p className="text-center text-slate-500 py-6">
                    Ningún mercaderista tiene rutas asignadas
                  </p>
                ) : (
                  <div className="space-y-2">
                    {usersWithRoutes.map(user => (
                      <div
                        key={user.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedUser?.id === user.id
                            ? 'bg-emerald-50 border-emerald-300'
                            : 'hover:bg-slate-50 border-slate-200'
                        }`}
                        onClick={() => handleSelectUser(user)}
                        data-testid={`user-item-${user.id}`}
                      >
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{user.name}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Route className="w-3 h-3" />
                            <span>{user.route_count} ruta(s)</span>
                            <span>•</span>
                            <Store className="w-3 h-3" />
                            <span>{user.total_stores} tiendas</span>
                          </div>
                        </div>
                        {/* Route badges */}
                        <div className="flex flex-wrap gap-1 max-w-[120px]">
                          {user.routes?.slice(0, 2).map((r, i) => (
                            <Badge key={`${user.id}-route-${r.id || r.route_order || i}`} variant="outline" className="text-xs">
                              {r.route_order}. {r.template_name?.slice(0, 8)}...
                            </Badge>
                          ))}
                          {user.routes?.length > 2 && (
                            <Badge variant="outline" className="text-xs">+{user.routes.length - 2}</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Users without routes */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Sin Rutas Asignadas
                </CardTitle>
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  {usersWithoutRoutes.length}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[250px]">
                {usersWithoutRoutes.length === 0 ? (
                  <p className="text-center text-emerald-600 py-6">
                    <Check className="w-8 h-8 mx-auto mb-2" />
                    Todos los mercaderistas tienen rutas
                  </p>
                ) : (
                  <div className="space-y-2">
                    {usersWithoutRoutes.map(user => (
                      <div
                        key={user.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedUser?.id === user.id
                            ? 'bg-amber-50 border-amber-300'
                            : 'border-amber-200 bg-amber-50/50 hover:bg-amber-50'
                        }`}
                        onClick={() => handleSelectUser(user)}
                        data-testid={`user-no-route-${user.id}`}
                      >
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{user.name}</p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Right: User Routes Detail */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  {selectedUser ? `Rutas de ${selectedUser.name}` : 'Detalle de Rutas'}
                </CardTitle>
                {userRoutesData && (
                  <CardDescription>
                    {userRoutesData.total_routes} ruta(s), {userRoutesData.total_stores} tiendas en total
                  </CardDescription>
                )}
              </div>
              {selectedUser && (
                <Button 
                  size="sm" 
                  onClick={handleOpenAddRouteDialog}
                  disabled={templates.length === 0 || getAvailableTemplates().length === 0}
                  data-testid="add-route-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Agregar Ruta
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedUser ? (
              <div className="text-center py-16 text-slate-500">
                <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-600 mb-2">Selecciona un mercaderista</h3>
                <p className="text-slate-500">Para ver y gestionar sus rutas</p>
              </div>
            ) : !userRoutesData?.routes?.length ? (
              <div className="text-center py-16 text-slate-500">
                <Route className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Sin rutas asignadas</p>
                <p className="text-sm mb-4">Agrega rutas a este mercaderista</p>
                <Button 
                  onClick={handleOpenAddRouteDialog}
                  disabled={templates.length === 0}
                >
                  <Plus className="w-4 h-4 mr-2" /> Agregar Primera Ruta
                </Button>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="space-y-4">
                  {userRoutesData.routes.map((route, routeIndex) => (
                    <div 
                      key={route.assignment_id}
                      className="border rounded-lg overflow-hidden"
                      data-testid={`user-route-${route.assignment_id}`}
                    >
                      {/* Route Header */}
                      <div className="bg-slate-100 px-4 py-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                            {route.route_order}
                          </div>
                          <div>
                            <span className="font-medium">{route.template_name}</span>
                            <span className="text-xs text-slate-500 ml-2">
                              ({route.store_count} tiendas)
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* Reorder buttons */}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={routeIndex === 0}
                            onClick={() => handleMoveRouteUp(routeIndex)}
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={routeIndex === userRoutesData.routes.length - 1}
                            onClick={() => handleMoveRouteDown(routeIndex)}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-500 hover:text-red-700"
                            onClick={() => handleRemoveRoute(route.assignment_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Route Stores */}
                      <div className="p-2 space-y-1 max-h-[150px] overflow-y-auto">
                        {route.stores?.map((item, storeIndex) => (
                          <div
                            key={item.id}
                            className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm"
                          >
                            <div className="w-5 h-5 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-medium">
                              {storeIndex + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="truncate block">
                                {item.store?.name || 'N/A'}
                              </span>
                            </div>
                            {item.chain?.name && (
                              <Badge variant="outline" className="text-xs shrink-0">
                                {item.chain.name}
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Next route indicator */}
                      {routeIndex < userRoutesData.routes.length - 1 && (
                        <div className="px-4 py-1 bg-blue-50 text-center text-xs text-blue-600 flex items-center justify-center gap-1">
                          <ArrowRight className="w-3 h-3" />
                          Al completar, continúa con Ruta {userRoutesData.routes[routeIndex + 1].route_order}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Cycle indicator */}
                  {userRoutesData.routes.length > 1 && (
                    <div className="text-center py-2 text-xs text-slate-500 flex items-center justify-center gap-1">
                      <RotateCcw className="w-3 h-3" />
                      Al completar todas, vuelve a Ruta 1
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Route Dialog */}
      <Dialog open={showAddRouteDialog} onOpenChange={setShowAddRouteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Agregar Ruta a {selectedUser?.name}
            </DialogTitle>
            <DialogDescription>
              Selecciona una plantilla de ruta para agregar
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
              <SelectTrigger data-testid="select-template">
                <SelectValue placeholder="Selecciona una plantilla de ruta" />
              </SelectTrigger>
              <SelectContent>
                {getAvailableTemplates().map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    <div className="flex items-center gap-2">
                      <Route className="w-4 h-4" />
                      <span>{t.name}</span>
                      <span className="text-slate-400 text-xs">({t.store_count} tiendas)</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {userRoutesData?.routes?.length > 0 && (
              <p className="text-sm text-slate-500 mt-3">
                Esta ruta se agregará como <strong>Ruta {userRoutesData.routes.length + 1}</strong>
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRouteDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddRoute} data-testid="confirm-add-route-btn">
              <Plus className="w-4 h-4 mr-2" /> Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
